import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, of } from 'rxjs';
import { extractIsoDate, formatDateIsoToBr, formatIsoDateTime } from '../utils/date-format.util';
import { IntegracaoParticipanteStatus } from '../enum/integracao-participante-status.enum';
import {
  AdesaoIntegracao,
  AdesaoLog,
  AdesaoIntegracaoFiltros,
  IntegracaoLog,
} from '../models/integracao.model';
import {
  CampanhaIntegracaoParticipanteDto,
  CampanhaIntegracoesQueryParams,
  CampanhaIntegracoesResponseDto,
  CampanhaLogIntegracaoResponseDto,
} from '../api/models';
import { IntegracaoApiService } from './integracao-api.service';

const SOMENTE_DIGITOS = /^\d+$/;

@Injectable({
  providedIn: 'root',
})
export class IntegracaoService {
  constructor(private readonly integracaoApiService: IntegracaoApiService) {}

  /** Participantes da campanha escolhida na tela de seleção. */
  listarAdesoes(
    idIntegracao: number | string,
    filtros?: AdesaoIntegracaoFiltros,
  ): Observable<AdesaoIntegracao[]> {
    return this.buscarParticipantes(idIntegracao, filtros).pipe(
      map((dtos) => this.filtrarParticipantes(dtos, filtros)),
      map((dtos) => dtos.map((dto) => this.mapParticipante(dto))),
      map((adesoes) => this.filtrarPorStatus(adesoes, filtros)),
    );
  }

  /**
   * O endpoint não aceita `status` como parâmetro (fora do swagger), então o filtro de Status
   * sempre recorta em memória sobre o `dsStatus` que já vem em cada linha.
   */
  private filtrarPorStatus(
    adesoes: AdesaoIntegracao[],
    filtros?: AdesaoIntegracaoFiltros,
  ): AdesaoIntegracao[] {
    if (!filtros?.status) {
      return adesoes;
    }

    return adesoes.filter((adesao) => adesao.status === filtros.status);
  }

  /**
   * Um termo numérico pode ser matrícula ou registro, e o endpoint combina os parâmetros entre si:
   * mandar os dois na mesma URL só acharia quem tivesse aquele número nos dois campos. Por isso
   * são duas chamadas, uma por campo, unidas aqui.
   */
  private buscarParticipantes(
    idCampanha: number | string,
    filtros?: AdesaoIntegracaoFiltros,
  ): Observable<CampanhaIntegracaoParticipanteDto[]> {
    const params = this.montarParams(filtros);
    const numero = this.termoNumerico(filtros);

    if (!numero) {
      return this.integracaoApiService
        .getIntegracoesDaCampanha(idCampanha, params)
        .pipe(map((response) => response.dados ?? []));
    }

    return forkJoin([
      this.integracaoApiService.getIntegracoesDaCampanha(idCampanha, {
        ...params,
        numMatrPartf: numero,
      }),
      this.integracaoApiService.getIntegracoesDaCampanha(idCampanha, {
        ...params,
        numRgtroEmprg: numero,
      }),
    ]).pipe(
      map(([porMatricula, porRegistro]) =>
        this.unirParticipantes(porMatricula.dados ?? [], porRegistro.dados ?? []),
      ),
    );
  }

  /**
   * Quem tem o mesmo número como matrícula e como registro volta nas duas buscas. A linha só pode
   * aparecer uma vez — repetida, a lista ainda quebraria o `track` do `@for` por id duplicado.
   */
  private unirParticipantes(
    ...listas: CampanhaIntegracaoParticipanteDto[][]
  ): CampanhaIntegracaoParticipanteDto[] {
    const porChave = new Map<string, CampanhaIntegracaoParticipanteDto>();

    for (const dto of listas.flat()) {
      const chave = `${dto.idPerfilSelPart ?? ''}-${dto.idPerfInvestPartic ?? ''}`;
      if (!porChave.has(chave)) {
        porChave.set(chave, dto);
      }
    }

    return [...porChave.values()];
  }

  /** O termo da busca quando é só dígitos — aí ele pode ser matrícula ou registro. */
  private termoNumerico(filtros?: AdesaoIntegracaoFiltros): string | null {
    const termo = filtros?.participante?.trim();

    return termo && SOMENTE_DIGITOS.test(termo) ? termo : null;
  }

  /**
   * Libera para integração o que a tela marcou. O endpoint aceita um participante por chamada —
   * não uma lista —, então é uma chamada por adesão selecionada, escopada por
   * `idPerfilInvestimentoParticipante` (toda linha tem; `codCltrprf` vai também quando a linha
   * tiver).
   */
  liberarAdesoes(
    idCampanha: number | string,
    adesoes: readonly AdesaoIntegracao[],
  ): Observable<void> {
    if (adesoes.length === 0) {
      return of(undefined);
    }

    return forkJoin(
      adesoes.map((adesao) =>
        this.integracaoApiService.integrarPerfisParticipantes(
          idCampanha,
          adesao.idPerfilInvestimentoParticipante,
          adesao.codCltrprf,
        ),
      ),
    ).pipe(map(() => undefined));
  }

  /** Resumo da carga, servido pelo mesmo endpoint da lista. */
  obterLog(idIntegracao: number | string): Observable<IntegracaoLog> {
    // Sem filtros de propósito: o resumo descreve a carga inteira, não o recorte da tela.
    return this.integracaoApiService
      .getIntegracoesDaCampanha(idIntegracao)
      .pipe(map((response) => this.mapResumoDaCampanha(response)));
  }

  /**
   * Histórico de um participante, com a mensagem que explica cada status. Recebe a adesão inteira
   * porque o endpoint devolve só as entradas: a chave da URL (`idPerfInvestPartic`) e o nome que
   * o diálogo exibe no cabeçalho vêm da linha que abriu o log.
   */
  obterLogAdesao(
    idCampanha: number | string,
    adesao: AdesaoIntegracao,
  ): Observable<AdesaoLog> {
    return this.integracaoApiService
      .getLogDoParticipante(idCampanha, adesao.idPerfilInvestimentoParticipante)
      .pipe(map((dtos) => this.mapLogAdesao(adesao, dtos)));
  }

  private mapLogAdesao(
    adesao: AdesaoIntegracao,
    dtos: CampanhaLogIntegracaoResponseDto[],
  ): AdesaoLog {
    return {
      idAdesao: adesao.id,
      nome: adesao.nome,
      entradas: (dtos ?? []).map((dto) => ({
        dataHora: formatIsoDateTime(dto.dtInclusao),
        dataIntegracao: dto.dtIntegracao ? formatIsoDateTime(dto.dtIntegracao) : null,
        status: dto.descLog ?? '',
        mensagem: dto.msg ?? '',
      })),
    };
  }

  /**
   * Parâmetros comuns às duas formas de busca. Um termo com letras só pode ser nome; o numérico
   * fica de fora porque `buscarParticipantes` o manda em uma chamada por campo.
   */
  private montarParams(filtros?: AdesaoIntegracaoFiltros): CampanhaIntegracoesQueryParams {
    const params: CampanhaIntegracoesQueryParams = {};
    const termo = filtros?.participante?.trim();

    if (termo && !SOMENTE_DIGITOS.test(termo)) {
      params.nomeEmpreg = termo;
    }

    return params;
  }

  /**
   * Traduz o participante vindo de `/v1/Campanha/{id}/Integracoes`, cujos campos chegam com os
   * nomes das colunas do legado. `idPerfilSelPart` é o que identifica a adesão nas ações da tela.
   */
  private mapParticipante(dto: CampanhaIntegracaoParticipanteDto): AdesaoIntegracao {
    const dataIntegracao = extractIsoDate(dto.dtIntegracao);

    return {
      id: dto.idPerfilSelPart ?? 0,
      idPerfilInvestimentoParticipante: dto.idPerfInvestPartic ?? 0,
      nome: dto.nomEmprg ?? '',
      matricula: dto.numMatrPartf ?? null,
      registro: dto.numRgtroEmprg ?? null,
      plano: dto.numPlbnf ?? null,
      perfilAnterior: dto.nmPerfilAnterior ?? '',
      idPerfilAnterior: dto.idPerfilAnt ?? null,
      perfilNovo: dto.nmPerfilNovo ?? '',
      idPerfilNovo: dto.codPerfilNovo ?? null,
      status: this.mapStatus(dto.dsStatus),
      dataIntegracao: dataIntegracao ? formatDateIsoToBr(dataIntegracao) : null,
      codCltrprf: dto.codCltrprf ?? null,
    };
  }

  /**
   * `dsStatus` já chega decodificado do back (`DECODE` na procedure): 'Erro', 'Sucesso' ou
   * 'Aguardando', casando direto com o enum. Qualquer outra coisa cai em Aguardando.
   */
  private mapStatus(dsStatus?: string | null): IntegracaoParticipanteStatus {
    const valores = Object.values(IntegracaoParticipanteStatus) as string[];
    const texto = dsStatus?.trim();

    return texto && valores.includes(texto)
      ? (texto as IntegracaoParticipanteStatus)
      : IntegracaoParticipanteStatus.Aguardando;
  }

  /**
   * Resumo da carga, que vem na mesma resposta da lista. `pendenteIntegracao` ainda não é
   * enviado: até chegar, o pendente é o que sobra do total depois de integrados e falhas.
   * `execucoes` também não vem — este endpoint não expõe o histórico de disparos, então o
   * diálogo de log fica sem execuções até o back oferecê-lo.
   */
  private mapResumoDaCampanha(response: CampanhaIntegracoesResponseDto): IntegracaoLog {
    const total = response.resumo?.totalRegistros ?? 0;
    const integrados = response.resumo?.integrados ?? 0;
    const erros = response.resumo?.falhas ?? 0;

    return {
      total,
      integrados,
      erros,
      pendentes: response.resumo?.pendenteIntegracao ?? Math.max(total - integrados - erros, 0),
      execucoes: [],
    };
  }

  /**
   * Rede de proteção para o filtro de nome/matrícula/registro, que o endpoint recebe mas pode
   * ainda não aplicar (mesmo padrão de `CampanhaService`), mais o de data, que não tem parâmetro
   * no endpoint. Status é filtrado depois, sobre o domínio já mapeado (`filtrarPorStatus`).
   */
  private filtrarParticipantes(
    lista: CampanhaIntegracaoParticipanteDto[],
    filtros?: AdesaoIntegracaoFiltros,
  ): CampanhaIntegracaoParticipanteDto[] {
    if (!filtros) {
      return lista;
    }

    return lista.filter((dto) => {
      const termo = filtros.participante?.trim();
      if (termo && !this.casaComTermo([dto.nomEmprg, dto.numMatrPartf, dto.numRgtroEmprg], termo)) {
        return false;
      }

      if (filtros.data && extractIsoDate(dto.dtIntegracao) !== filtros.data) {
        return false;
      }

      return true;
    });
  }

  /**
   * Campo de busca unificado: um termo só procura em nome, matrícula e registro. Nome casa por
   * trecho e sem diferenciar maiúsculas; os números casam por trecho também, para a pessoa
   * poder digitar só o começo da matrícula.
   */
  private casaComTermo(
    campos: readonly (string | number | null | undefined)[],
    termo: string,
  ): boolean {
    const busca = termo.trim().toLowerCase();

    return campos
      .filter((campo) => campo != null)
      .some((campo) => String(campo).toLowerCase().includes(busca));
  }
}
